import React, { useState, useEffect } from 'react';
import { FiUser, FiUserPlus, FiMapPin, FiCalendar, FiBookOpen, FiAward, FiBriefcase, FiRefreshCw } from 'react-icons/fi';

const PeopleYouMayKnow = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/people-you-may-know?limit=8`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch suggestions');
      }

      const data = await response.json();
      setSuggestions(data.suggestions || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setError('Failed to load suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const viewProfile = (alumniId) => {
    window.open(`/alumni/profile/${alumniId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <FiUser className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Unable to load suggestions</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <button
            onClick={() => fetchSuggestions()}
            className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FiRefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <FiUserPlus className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No suggestions available</h3>
          <p className="mt-1 text-sm text-gray-500">
            We'll suggest connections as more alumni join and update their profiles.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">People You May Know</h2>
            <p className="text-sm text-gray-600">Connect with fellow alumni</p>
          </div>
          <button
            onClick={() => fetchSuggestions(true)}
            disabled={refreshing}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiRefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Suggestions Grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {suggestions.map((person) => (
            <SuggestionCard key={person.id} person={person} onViewProfile={viewProfile} />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Showing {suggestions.length} suggestions</span>
          <button
            onClick={() => window.open('/alumni/directory', '_blank')}
            className="font-medium text-blue-600 hover:text-blue-500"
          >
            Browse All Alumni →
          </button>
        </div>
      </div>
    </div>
  );
};

const SuggestionCard = ({ person, onViewProfile }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
      <div className="flex items-start space-x-4">
        {/* Profile Photo */}
        <div className="flex-shrink-0">
          {person.headshot_url ? (
            <img
              className="h-12 w-12 rounded-full object-cover"
              src={person.headshot_url}
              alt={`${person.first_name} ${person.last_name}`}
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
              <FiUser className="h-6 w-6 text-gray-600" />
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-900 truncate">
            {person.first_name} {person.last_name}
          </h3>
          
          {/* Current Position & Company */}
          {person.show_current_position && person.current_position && (
            <p className="text-xs text-gray-600 truncate">{person.current_position}</p>
          )}
          {person.show_current_employer && person.current_employer && (
            <div className="flex items-center mt-1">
              {person.current_company_logo && (
                <img
                  src={person.current_company_logo}
                  alt={person.current_employer}
                  className="h-3 w-3 rounded mr-1 bg-white"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <p className="text-xs text-blue-600 font-medium truncate">{person.current_employer}</p>
            </div>
          )}

          {/* Match Reasons */}
          {person.match_reasons && person.match_reasons.length > 0 && (
            <div className="mt-2">
              <div className="flex flex-wrap gap-1">
                {person.match_reasons.slice(0, 2).map((reason, index) => (
                  <span
                    key={index}
                    className="inline-block px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full"
                  >
                    {reason}
                  </span>
                ))}
                {person.match_reasons.length > 2 && (
                  <span className="text-xs text-gray-500">
                    +{person.match_reasons.length - 2} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Quick Info */}
          <div className="mt-2 flex items-center space-x-3 text-xs text-gray-500">
            {person.show_graduation_year && person.graduation_year && (
              <div className="flex items-center">
                <FiCalendar className="h-3 w-3 mr-1" />
                <span>'{String(person.graduation_year).slice(-2)}</span>
              </div>
            )}
            {person.show_major && person.major && (
              <div className="flex items-center">
                <FiBookOpen className="h-3 w-3 mr-1" />
                <span className="truncate">{person.major.split(' ').slice(0, 2).join(' ')}</span>
              </div>
            )}
          </div>

          {/* View Profile Button */}
          <div className="mt-3">
            <button
              onClick={() => onViewProfile(person.id)}
              className="w-full text-center text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 py-1.5 px-3 rounded-md transition-colors"
            >
              View Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeopleYouMayKnow;